import { apiClient } from './apiClient';
import { ApiResponse } from './apiService';

export interface Notification {
  id: number;
  userId: number;
  type: string;
  title: string;
  message: string;
  relatedEntityType?: string | null;
  relatedEntityId?: number | null;
  read: boolean;
  createdAt: Date | string;
}

export interface NotificationQueryParams {
  page?: number;
  limit?: number;
  type?: string;
  read?: boolean;
}

export interface PaginatedNotificationsResponse {
  success: boolean;
  message: string;
  data: Notification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UnreadCountResponse {
  success: boolean;
  message: string;
  data: {
    count: number;
  };
}

class NotificationsService {
  private baseUrl = '/notifications';
  private pollingInterval: NodeJS.Timeout | null = null;
  private pollingCallbacks: ((count: number) => void)[] = [];

  async getNotifications(params?: NotificationQueryParams): Promise<PaginatedNotificationsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.type) queryParams.append('type', params.type);
      if (params?.read !== undefined) queryParams.append('read', params.read.toString());

      const queryString = queryParams.toString();
      const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
      const response = await apiClient.get<PaginatedNotificationsResponse>(url);
      return response.data;
    } catch (error) {
      console.error('Error al obtener notificaciones:', error);
      throw error;
    }
  }

  async getUnreadCount(): Promise<number> {
    try {
      const response = await apiClient.get<UnreadCountResponse>(`${this.baseUrl}/unread-count`);
      return response.data.data.count;
    } catch (error) {
      console.error('Error al obtener contador de notificaciones:', error);
      return 0;
    }
  }

  async markAsRead(id: number): Promise<Notification> {
    try {
      const response = await apiClient.patch<ApiResponse<Notification>>(`${this.baseUrl}/${id}/read`);
      return response.data.data;
    } catch (error) {
      console.error('Error al marcar notificación como leída:', error);
      throw error;
    }
  }

  async markAllAsRead(): Promise<void> {
    try {
      await apiClient.patch(`${this.baseUrl}/mark-all-read`);
    } catch (error) {
      console.error('Error al marcar todas las notificaciones como leídas:', error);
      throw error;
    }
  }

  async deleteNotification(id: number): Promise<void> {
    try {
      await apiClient.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error('Error al eliminar notificación:', error);
      throw error;
    }
  }

  startPolling(callback: (count: number) => void, intervalMs: number = 30000): void {
    this.pollingCallbacks.push(callback);

    if (!this.pollingInterval) {
      const poll = async () => {
        const count = await this.getUnreadCount();
        this.pollingCallbacks.forEach((cb) => cb(count));
      };

      poll();

      this.pollingInterval = setInterval(poll, intervalMs);
    }
  }

  stopPolling(callback?: (count: number) => void): void {
    if (callback) {
      this.pollingCallbacks = this.pollingCallbacks.filter((cb) => cb !== callback);
    } else {
      this.pollingCallbacks = [];
    }

    if (this.pollingCallbacks.length === 0 && this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  stopAllPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.pollingCallbacks = [];
  }
}

export const notificationsService = new NotificationsService();
